/** Baseline PII key deny-list, always applied. Extend (never replace) via {@link RedactionConfig.keys}. */
export const DEFAULT_PII_KEYS = [
    'password',
    'pass',
    'token',
    'accessToken',
    'refreshToken',
    'idToken',
    'sessionToken',
    'authorization',
    'apiKey',
    'secret',
    'clientSecret',
    'email',
    'phone',
    'ssn',
    'otp',
    'candidateId',
    'candidate_id',
    'firstName',
    'first_name',
    'lastName',
    'last_name',
    'creditCard',
    'cardNumber',
];
const DEFAULT_PLACEHOLDER = '[redacted]';
const DEFAULT_MAX_DEPTH = 6;
/** Merge the built-in PII keys with the caller's config into tslog `mask.*` settings. */
export function buildMaskSettings(config) {
    const keys = Array.from(new Set([...DEFAULT_PII_KEYS, ...(config?.keys ?? [])]));
    const settings = {
        keys,
        caseInsensitive: config?.caseInsensitive ?? true,
        placeholder: config?.placeholder ?? DEFAULT_PLACEHOLDER,
    };
    if (config?.patterns && config.patterns.length > 0) {
        settings.regex = config.patterns;
    }
    return settings;
}
function matchesKey(key, keys, caseInsensitive) {
    if (caseInsensitive) {
        const lowered = key.toLowerCase();
        return keys.some((k) => k.toLowerCase() === lowered);
    }
    return keys.includes(key);
}
function redactString(value, patterns, placeholder) {
    return patterns.reduce((acc, pattern) => acc.replace(pattern, placeholder), value);
}
/**
 * Deep-clone `value`, replacing any property whose key is in the deny-list, and scrubbing string
 * values that match a pattern. Cycles and over-deep structures collapse to the placeholder rather
 * than throw. Pure and tslog-free.
 */
export function redactValue(value, config) {
    const keys = Array.from(new Set([...DEFAULT_PII_KEYS, ...(config?.keys ?? [])]));
    const caseInsensitive = config?.caseInsensitive ?? true;
    const placeholder = config?.placeholder ?? DEFAULT_PLACEHOLDER;
    const patterns = config?.patterns ?? [];
    const maxDepth = config?.maxDepth ?? DEFAULT_MAX_DEPTH;
    const seen = new WeakSet();
    const walk = (input, depth) => {
        if (typeof input === 'string') {
            return patterns.length > 0 ? redactString(input, patterns, placeholder) : input;
        }
        if (input === null || typeof input !== 'object')
            return input;
        if (depth >= maxDepth)
            return placeholder;
        if (seen.has(input))
            return placeholder;
        seen.add(input);
        if (Array.isArray(input)) {
            return input.map((item) => walk(item, depth + 1));
        }
        const out = {};
        for (const [key, val] of Object.entries(input)) {
            out[key] = matchesKey(key, keys, caseInsensitive) ? placeholder : walk(val, depth + 1);
        }
        return out;
    };
    return walk(value, 0);
}
